import { isAxiosError } from 'axios';
import get from 'lodash.get';
import { match } from 'ts-pattern';

import log from '~/common/utils/log';
import {
  checkGeoComplyLocation,
  getGeoComplyLicense,
  type GeoComplyLocationCheckResponse,
} from '~/services/location/location.service.api';

export const enum CheckRequestStatus {
  ALLOWED = 'ALLOWED',
  DENIED = 'DENIED',
}

export const enum LocationCheckErrorType {
  PERMISSION_REQUIRED = 'PERMISSION_REQUIRED',
  INVALID_LOCATION = 'INVALID_LOCATION',
  UNDER_AGE = 'UNDER_AGE',
  VERIFICATION_FAILED = 'VERIFICATION_FAILED',
}

export type LocationCheckError = {
  code?: string | number;
  description: string;
  type?: LocationCheckErrorType;
  troubleshooters?: string[];
};

export type CheckRequestError = LocationCheckError;

// A genuine location verdict that denies the user (restricted location or under-age),
// as opposed to a token we simply could not obtain (timeout, permission, verification).
export const isGeoRestrictionError = (error: LocationCheckError) =>
  error.type === LocationCheckErrorType.INVALID_LOCATION ||
  error.type === LocationCheckErrorType.UNDER_AGE;

export type LocationTokenData = {
  userId: string;
  status: CheckRequestStatus;
  errors: LocationCheckError[];
  timestamp: string;
  country?: string;
  state?: string;
};

export type LocationToken = {
  token: string;
  data: LocationTokenData;
  expiresAt: number;
};

type BrowserGuardEvents = {
  on(eventName: string, callback: (...args: unknown[]) => void): BrowserGuardEvents;
};

type BrowserGuardClient = {
  events: BrowserGuardEvents;
  setLicense(license: string): void;
  setUserId(userId: string): void;
  setReason(reason: string): void;
  setReasonCode(reasonCode: string | undefined): void;
  setEarlyScan?(isEnabled: boolean): void;
  enableEarlyScan?(): void;
  startEarlyScan?(): void;
  setUserSessionId?(userSessionId: string): void;
  setLang?(lang: string): void;
  request(): void;
  requestWithTimeout?(timeoutMs: number): void;
  abort?(): void;
};

type GeoComplyGlobal = {
  createClient(
    options: unknown,
    logger?: (timestamp: string, message: unknown, level: string) => void
  ): BrowserGuardClient;
  startMyIpService?(): void;
  stopMyIpService?(): void;
  ackMyIpSuccess?(): void;
  onMyIpSuccess?: (ipAddress?: string) => void;
  onMyIpFailure?: (error?: unknown) => void;
};

declare global {
  interface Window {
    GcHtml5?: GeoComplyGlobal;
  }
}

export type GeoComplyCheckOptions = {
  reason?: string;
  reasonCode?: string;
  timeoutMs?: number;
  requireOwnCheck?: boolean;
};

type RestrictedLocationVerdict = {
  kind: 'restrictedLocationVerdict';
  locationError: LocationCheckError;
  token: string;
};

const isRestrictedLocationVerdict = (error: unknown): error is RestrictedLocationVerdict =>
  get(error, 'kind') === 'restrictedLocationVerdict';

const DEFAULT_REASON = 'Splash Sports location verification';
const DEFAULT_REASON_CODE = '2';
export const GEO_COMPLY_IP_CHANGE_REASON_CODE = '4';
const GEO_COMPLY_REASON_NAME_BY_CODE: Partial<Record<string, string>> = {
  '1': 'LOGIN',
  '2': 'PREWAGER',
  '10a': 'PAYMENT_INFORMATION_FUNDING_METHOD_ADD',
  '11a': 'TRANSACTION_DEPOSIT',
  '11b': 'TRANSACTION_WITHDRAWAL',
  [GEO_COMPLY_IP_CHANGE_REASON_CODE]: 'IPCHANGE',
};
const DEFAULT_TIMEOUT_MS = 60_000;
const LOCATION_ERROR_CODE = 'LOCATION_ERROR';
const AGE_RESTRICTION_ERROR_CODE = 'AGE_RESTRICTION';
const LICENSE_EXPIRY_BUFFER_MS = 3 * 60 * 1_000;

const getReason = ({ reason, reasonCode }: GeoComplyCheckOptions) =>
  reason ?? GEO_COMPLY_REASON_NAME_BY_CODE[reasonCode ?? ''] ?? DEFAULT_REASON;

const locationTokenDataChangeListeners = new Set<VoidFunction>();
const assetLoadPromises = new Map<string, Promise<void>>();

const getCdnConfig = () => ({
  cdnId: process.env.NEXT_PUBLIC_GEOCOMPLY_CDN_ID,
  cdnRoot: process.env.NEXT_PUBLIC_GEOCOMPLY_CDN_ROOT,
});

const getCheckedLocationData = (response: GeoComplyLocationCheckResponse) => ({
  ...response.data,
  ...response.data.data,
});

const enableEarlyScan = (client: BrowserGuardClient) => {
  client.setEarlyScan?.(true);
  client.enableEarlyScan?.();
  client.startEarlyScan?.();
};

const readResponseError = (error: unknown) => {
  if (!isAxiosError(error)) return undefined;

  const data: unknown = error.response?.data;
  const type = get(data, 'type', '') || get(data, 'error', '');
  const message = get(data, 'message', '');
  const nestedData: unknown = get(data, 'data');

  return {
    type: type || undefined,
    message: message || undefined,
    data: nestedData,
  };
};

const readTroubleshooters = (data: unknown): string[] => {
  const troubleshooters: unknown = get(data, 'troubleshooterMessages');
  return Array.isArray(troubleshooters)
    ? troubleshooters.filter((message): message is string => typeof message === 'string')
    : [];
};

const isLocationCheckError = (value: unknown): value is LocationCheckError =>
  !!get(value, 'description', '');

const normalizeLocationCheckError = (error: unknown): LocationCheckError => {
  const responseError = readResponseError(error);

  if (responseError) {
    return match(responseError.type)
      .with(LOCATION_ERROR_CODE, () => ({
        code: LOCATION_ERROR_CODE,
        description: responseError.message ?? 'Location verification denied the current location',
        type: LocationCheckErrorType.INVALID_LOCATION,
        troubleshooters: readTroubleshooters(responseError.data),
      }))
      .with(AGE_RESTRICTION_ERROR_CODE, () => ({
        code: AGE_RESTRICTION_ERROR_CODE,
        description: responseError.message ?? 'User does not meet location age requirements',
        type: LocationCheckErrorType.UNDER_AGE,
      }))
      .otherwise(() => ({
        description:
          responseError.message ?? (error instanceof Error ? error.message : String(error)),
        type: LocationCheckErrorType.VERIFICATION_FAILED,
      }));
  }

  if (isLocationCheckError(error)) {
    return {
      ...error,
      type: error.type ?? LocationCheckErrorType.VERIFICATION_FAILED,
    };
  }

  return {
    description: error instanceof Error ? error.message : String(error),
    type: LocationCheckErrorType.VERIFICATION_FAILED,
  };
};

const appendScript = (id: string, src: string) => {
  if (document.getElementById(id)) return Promise.resolve();

  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load GeoComply script: ${src}`));
    document.head.appendChild(script);
  });

  assetLoadPromises.set(id, promise);
  return promise;
};

const loadOnce = (id: string, load: () => Promise<void>) => assetLoadPromises.get(id) ?? load();

const toEpochMs = (value: string | number | undefined) => {
  if (typeof value === 'number') return value;
  return typeof value === 'string' ? Date.parse(value) : NaN;
};

class GeoComplySingleton {
  private static userId: string | null | undefined;

  private static locationToken: string | null;

  private static locationTokenDecoded: LocationToken | null;

  private static checkLocationPromise: Promise<string> | undefined;

  private static cachedLicenseData: { license: string; expires: string } | null = null;

  private static licenseExpiresAt = 0;

  private static isIpChangeDetectionActive = false;

  private static startIpChangeDetectionPromise: Promise<void> | undefined;

  private static GET_LOCATION_TOKEN_KEY() {
    return `geoComplyLocationToken-${this.userId}`;
  }

  private static GET_USER_SESSION_ID_KEY() {
    return `geoComplyUserSessionId-${this.userId}`;
  }

  private static getUserSessionId() {
    const key = this.GET_USER_SESSION_ID_KEY();
    const savedSessionId = sessionStorage.getItem(key);

    if (savedSessionId) return savedSessionId;

    const sessionId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    sessionStorage.setItem(key, sessionId);
    return sessionId;
  }

  private static saveToken(serializedToken: string | null) {
    if (!serializedToken) {
      this.locationToken = null;
      this.locationTokenDecoded = null;
      return;
    }

    try {
      const decoded = JSON.parse(serializedToken) as LocationToken;
      this.locationToken = decoded.token;
      this.locationTokenDecoded = decoded;
    } catch {
      this.locationToken = null;
      this.locationTokenDecoded = null;
    }
  }

  private static persistToken(token: string, data: LocationTokenData, expiresAt: number) {
    const locationToken: LocationToken = {
      token,
      data,
      expiresAt,
    };
    const serializedToken = JSON.stringify(locationToken);

    this.saveToken(serializedToken);
    localStorage.setItem(this.GET_LOCATION_TOKEN_KEY(), serializedToken);
    locationTokenDataChangeListeners.forEach((listener) => listener());
  }

  private static persistLocationError(error: LocationCheckError) {
    const heldToken = this.locationToken;
    const heldTokenData = this.locationTokenDecoded;
    const hasUsableToken =
      Boolean(heldToken) &&
      heldTokenData?.data.status === CheckRequestStatus.ALLOWED &&
      (heldTokenData?.expiresAt ?? 0) > Date.now();

    if (!isGeoRestrictionError(error) && hasUsableToken) {
      // data.timestamp stays on the original verdict so the age of that verdict is readable.
      this.persistToken(
        heldToken,
        { ...heldTokenData.data, errors: [error] },
        heldTokenData.expiresAt
      );
      return;
    }

    this.locationToken = null;
    this.locationTokenDecoded = {
      token: '',
      data: this.buildErrorTokenData(error),
      expiresAt: 0,
    };
    localStorage.removeItem(this.GET_LOCATION_TOKEN_KEY());
    locationTokenDataChangeListeners.forEach((listener) => listener());
  }

  private static clearLocationToken() {
    this.locationToken = null;
    this.locationTokenDecoded = null;
    localStorage.removeItem(this.GET_LOCATION_TOKEN_KEY());
    locationTokenDataChangeListeners.forEach((listener) => listener());
  }

  private static async loadBrowserGuardAssets(cdnId: string, cdnRoot: string) {
    if (typeof window === 'undefined') {
      throw new Error('GeoComply Browser Guard can only run in a browser');
    }

    const baseUrl = `${cdnRoot}/${cdnId}`;

    await loadOnce(`geocomply-sdk-${cdnId}`, () =>
      appendScript(`geocomply-sdk-${cdnId}`, `${baseUrl}/gc-html5.js`)
    );

    if (!window.GcHtml5) {
      throw new Error('GeoComply Browser Guard did not initialize');
    }

    return false;
  }

  private static buildTokenData(
    locationCheckResponse: GeoComplyLocationCheckResponse
  ): LocationTokenData {
    const checkedLocationData = getCheckedLocationData(locationCheckResponse);

    return {
      userId: this.userId ?? '',
      status: CheckRequestStatus.ALLOWED,
      errors: [],
      timestamp: new Date().toISOString(),
      country: checkedLocationData.country,
      state: checkedLocationData.state,
    };
  }

  private static buildErrorTokenData(error: LocationCheckError): LocationTokenData {
    return {
      userId: this.userId ?? '',
      status: isGeoRestrictionError(error) ? CheckRequestStatus.DENIED : CheckRequestStatus.ALLOWED,
      errors: [error],
      timestamp: new Date().toISOString(),
    };
  }

  private static async getCachedOrFetchLicense() {
    if (this.cachedLicenseData && Date.now() < this.licenseExpiresAt - LICENSE_EXPIRY_BUFFER_MS) {
      return this.cachedLicenseData;
    }

    const licenseResponse = await getGeoComplyLicense('web');
    const { license, expires } = licenseResponse.data;

    if (license) {
      this.cachedLicenseData = { license, expires };
      this.licenseExpiresAt = Date.parse(expires);
    }

    return { license, expires };
  }

  private static async checkLocation(options: GeoComplyCheckOptions = {}): Promise<string> {
    const { requireOwnCheck, ...checkOptions } = options;

    if (requireOwnCheck && this.checkLocationPromise) {
      await this.checkLocationPromise.catch(() => undefined);
      return this.checkLocation(options);
    }

    if (!this.checkLocationPromise) {
      this.checkLocationPromise = this.runBrowserGuardCheck(checkOptions).finally(() => {
        this.checkLocationPromise = undefined;
      });
    }

    return this.checkLocationPromise;
  }

  private static async runBrowserGuardCheck(options: GeoComplyCheckOptions) {
    log('Fetching new GeoComply location token: ', new Date().toISOString());

    const { license, expires } = await this.getCachedOrFetchLicense();
    const { cdnId, cdnRoot } = getCdnConfig();

    if (!license || !cdnId || !cdnRoot) {
      throw new Error('GeoComply license response is missing license or CDN config');
    }

    await this.loadBrowserGuardAssets(cdnId, cdnRoot);

    const client = window.GcHtml5.createClient(null, (timestamp, message, level) => {
      log('GeoComply Browser Guard', { timestamp, message, level });
    });

    return new Promise<string>((resolve, reject) => {
      let settled = false;
      const timeout = window.setTimeout(() => {
        if (settled) return;
        settled = true;
        client.abort?.();
        const locationError = normalizeLocationCheckError(
          new Error('GeoComply location check timed out')
        );

        this.persistLocationError(locationError);
        reject(locationError);
      }, options.timeoutMs ?? DEFAULT_TIMEOUT_MS);

      const finishSuccess = async (token: string) => {
        if (settled) return;

        if (!token) {
          settled = true;
          window.clearTimeout(timeout);
          const locationError = normalizeLocationCheckError(
            new Error('GeoComply location check did not return a token')
          );

          this.persistLocationError(locationError);
          reject(locationError);
          return;
        }

        try {
          const locationCheckResponse = await checkGeoComplyLocation(token);
          if (settled) return;

          const checkedLocationData = getCheckedLocationData(locationCheckResponse);
          const expiresAt = toEpochMs(
            checkedLocationData.expiresAt ?? checkedLocationData.exp ?? expires
          );

          settled = true;
          window.clearTimeout(timeout);
          this.persistToken(
            token,
            this.buildTokenData(locationCheckResponse),
            Number.isNaN(expiresAt) ? 0 : expiresAt
          );
          resolve(token);
        } catch (error) {
          if (settled) return;

          const locationError = normalizeLocationCheckError(error);

          settled = true;
          window.clearTimeout(timeout);

          if (isGeoRestrictionError(locationError)) {
            this.persistToken(token, this.buildErrorTokenData(locationError), 0);
            const verdict: RestrictedLocationVerdict = {
              kind: 'restrictedLocationVerdict',
              locationError,
              token,
            };
            reject(verdict);
            return;
          }

          this.persistLocationError(locationError);
          reject(locationError);
        }
      };

      const finishFailure = (error: LocationCheckError) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        const locationError = normalizeLocationCheckError(error);

        this.persistLocationError(locationError);
        reject(locationError);
      };

      client.events
        .on('success', (text) => finishSuccess(String(text ?? '')))
        .on('failed', (code, message) =>
          finishFailure({
            code: typeof code === 'string' || typeof code === 'number' ? code : undefined,
            description: String(message),
          })
        )
        .on('abort', () => finishFailure({ description: 'GeoComply location check aborted' }))
        .on('browser.failed', (error) =>
          finishFailure({
            description: `Browser geolocation failed: ${String(error)}`,
            type: LocationCheckErrorType.PERMISSION_REQUIRED,
          })
        );

      client.setLicense(license);
      client.setUserId(this.userId);
      client.setReason(getReason(options));
      client.setReasonCode(options.reasonCode ?? DEFAULT_REASON_CODE);
      client.setUserSessionId?.(this.getUserSessionId());
      enableEarlyScan(client);

      if (client.requestWithTimeout) {
        client.requestWithTimeout(options.timeoutMs ?? DEFAULT_TIMEOUT_MS);
      } else {
        client.request();
      }
    });
  }

  private static async initializeIpChangeDetection(options: GeoComplyCheckOptions = {}) {
    if (!this.userId) {
      log('GeoComply IP change detection skipped. No user set', new Date().toISOString());
      return;
    }

    const { license } = await this.getCachedOrFetchLicense();
    const { cdnId, cdnRoot } = getCdnConfig();

    if (!license || !cdnId || !cdnRoot) {
      throw new Error('GeoComply license response is missing license or CDN config');
    }

    await this.loadBrowserGuardAssets(cdnId, cdnRoot);

    if (!window.GcHtml5.startMyIpService || !window.GcHtml5.stopMyIpService) {
      log('GeoComply MyIpService is unavailable', new Date().toISOString());
      return;
    }

    window.GcHtml5.onMyIpSuccess = () => {
      window.GcHtml5?.ackMyIpSuccess?.();
      log('GeoComply IP change detected', new Date().toISOString());
      this.clearLocationToken();
      this.getValidLocationToken(0, true, {
        ...options,
        reasonCode: options.reasonCode ?? GEO_COMPLY_IP_CHANGE_REASON_CODE,
      }).then(({ errors }) => {
        if (errors) {
          log('GeoComply IP change location recheck failed', errors);
        }
      });
    };

    window.GcHtml5.onMyIpFailure = (error) => {
      log('GeoComply MyIpService failed', error);
    };

    window.GcHtml5.startMyIpService();
    this.isIpChangeDetectionActive = true;
  }

  static async init(userId?: string) {
    if (this.userId && this.userId !== userId) {
      this.stopIpChangeDetection();
    }

    this.userId = userId;
    this.saveToken(localStorage.getItem(this.GET_LOCATION_TOKEN_KEY()));
  }

  static setUserId(userId: string) {
    this.userId = userId;
  }

  static subscribeToLocationToken(listener: VoidFunction) {
    locationTokenDataChangeListeners.add(listener);
    return () => {
      locationTokenDataChangeListeners.delete(listener);
    };
  }

  static getPersistedLocationToken(): string | null {
    return this.locationToken;
  }

  static getLocationTokenData(): LocationTokenData | null {
    return this.locationTokenDecoded?.data ?? null;
  }

  static async startIpChangeDetection(options: GeoComplyCheckOptions = {}) {
    if (this.isIpChangeDetectionActive) return;

    if (!this.startIpChangeDetectionPromise) {
      this.startIpChangeDetectionPromise = this.initializeIpChangeDetection(options).finally(() => {
        this.startIpChangeDetectionPromise = undefined;
      });
    }

    await this.startIpChangeDetectionPromise;
  }

  static stopIpChangeDetection() {
    this.isIpChangeDetectionActive = false;

    if (typeof window === 'undefined') return;

    window.GcHtml5?.stopMyIpService?.();
    if (window.GcHtml5) {
      window.GcHtml5.onMyIpSuccess = undefined;
      window.GcHtml5.onMyIpFailure = undefined;
    }
  }

  static invalidateToken() {
    const sessionIdKey = this.GET_USER_SESSION_ID_KEY();

    this.stopIpChangeDetection();
    this.locationToken = null;
    this.locationTokenDecoded = null;
    this.cachedLicenseData = null;
    this.licenseExpiresAt = 0;
    localStorage.removeItem(this.GET_LOCATION_TOKEN_KEY());
    sessionStorage.removeItem(sessionIdKey);
    this.userId = null;
  }

  static async getValidLocationToken(
    validAtLeastForMs = 0,
    forceRefetch = false,
    options: GeoComplyCheckOptions = {}
  ) {
    const savedLocationToken = this.locationToken;
    const savedTokenData = this.locationTokenDecoded?.data;

    if (
      !forceRefetch &&
      savedLocationToken &&
      savedTokenData?.status === CheckRequestStatus.ALLOWED &&
      !savedTokenData.errors.length &&
      this.locationTokenDecoded?.expiresAt > Date.now() + validAtLeastForMs
    ) {
      return { token: savedLocationToken };
    }

    if (!this.userId) {
      log('GeoComply location token fetch skipped. No user set', new Date().toISOString());
      return {
        errors: [{ description: 'userId not set, skipping GeoComply location check' }],
        token: null,
      };
    }

    try {
      const token = await this.checkLocation(options);
      return { token };
    } catch (error) {
      if (isRestrictedLocationVerdict(error)) {
        return { errors: [error.locationError], restrictedLocationToken: error.token };
      }

      return { errors: [normalizeLocationCheckError(error)] };
    }
  }
}

export default GeoComplySingleton;
